import React from 'react';
import { capitalize, Divider, MenuItem } from '@mui/material';
import ListItemIcon from '@mui/material/ListItemIcon';
import FolderIcon from '@mui/icons-material/Folder';
import PresentToAll from '@mui/icons-material/PresentToAll';
import EditIcon from '@mui/icons-material/Edit';
import AddBoxIcon from '@mui/icons-material/AddBox';
import ShareIcon from '@mui/icons-material/Share';
import DeleteIcon from '@mui/icons-material/Delete';
import GetAppIcon from '@mui/icons-material/GetApp';
import SendIcon from '@mui/icons-material/Send';
import CancelPresentation from '@mui/icons-material/CancelPresentation';
import StarIcon from '@mui/icons-material/Star';
import { StarOutline } from '@mui/icons-material';
import WidgetsOutlinedIcon from '@mui/icons-material/WidgetsOutlined';
import { useTranslation } from 'react-i18next';
import { DashboardActionType, DASHBOARD_ACTION_TYPE } from './helpers/actions';
import UnfoldLessIcon from '@mui/icons-material/UnfoldLess';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

/**
 * Get the icons associated the actions in the dashboard edit menu
 * @param {boolean} isInPresentationMode - Whether the dashboard is in presentation mode
 * @param {boolean} isFavourite - Whether the dashboard is a favourite for the user
 * @returns {Record<DashboardActionType, React.ReactNode>} the record of icons associated to the actions
 */
const getIcon = (
    isInPresentationMode: boolean,
    isFavourite: boolean,
    isFullWidth: boolean,
): Record<DashboardActionType, React.ReactNode> => ({
    rename: <EditIcon />,
    duplicate: <AddBoxIcon />,
    share: <ShareIcon />,
    archive: <DeleteIcon />,
    export: <GetAppIcon />,
    send: <SendIcon />,
    move: <FolderIcon />,
    presentationMode: isInPresentationMode ? (
        <CancelPresentation />
    ) : (
        <PresentToAll />
    ),
    favourite: isFavourite ? <StarIcon /> : <StarOutline />,
    editLayout: <WidgetsOutlinedIcon />,
    fullWidth: isFullWidth ? (
        <UnfoldLessIcon sx={{ transform: 'rotate(90deg)' }} />
    ) : (
        <UnfoldMoreIcon sx={{ transform: 'rotate(90deg)' }} />
    ),
    informations: <InfoOutlinedIcon />,
    create: null,
});

/**
 * The action label of the dashboard edit menu item is usally a simple translation key.
 * However, for the presentation mode and favourite actions, the label depends on the current state of the dashboard.
 * @param {DashboardActionType} action - The action of the dashboard edit menu item
 * @param {boolean} isInPresentationMode - Whether the dashboard is in presentation mode
 * @param {boolean} isFavourite - Whether the dashboard is a favourite for the user
 * @returns {string} the label of the dashboard edit menu item
 */
const getDashboardActionLabel = (
    action: DashboardActionType,
    isInPresentationMode: boolean,
    isFavourite: boolean,
    isFullWidth: boolean,
): string => {
    const presentationModeLabel = isInPresentationMode
        ? 'common.presentationModeLabels.enable'
        : 'common.presentationModeLabels.disable';

    const favouriteLabel = isFavourite
        ? 'common.favourite.remove'
        : 'common.favourite.add';

    const fullWidthLabel = isFullWidth
        ? 'common.fullWidth.disable'
        : 'common.fullWidth.enable';

    if (action === DASHBOARD_ACTION_TYPE.PresentationMode)
        return presentationModeLabel;

    if (action === DASHBOARD_ACTION_TYPE.Favourite) return favouriteLabel;

    if (action === DASHBOARD_ACTION_TYPE.FullWidth) return fullWidthLabel;

    return `dashboardEdit.${action}`;
};

interface DashboardEditMenuItemProps {
    action: DashboardActionType;
    onClickModal: (action: DashboardActionType) => () => void;
    isInPresentationMode: boolean;
    isFavourite: boolean;
    isFullWidth: boolean;
}

const DashboardEditMenuItem = ({
    action,
    onClickModal,
    isInPresentationMode,
    isFavourite,
    isFullWidth,
}: DashboardEditMenuItemProps) => {
    const { t } = useTranslation();
    const icons = getIcon(isInPresentationMode, isFavourite, isFullWidth);
    const icon = action in icons ? icons[action] : null;
    return (
        <MenuItem
            onClick={onClickModal(action)}
            className={`e2e-dashboard-${action}-button`}
        >
            <ListItemIcon>{icon}</ListItemIcon>
            {capitalize(
                t(
                    getDashboardActionLabel(
                        action,
                        isInPresentationMode,
                        isFavourite,
                        isFullWidth,
                    ),
                ),
            )}
        </MenuItem>
    );
};

interface DashboardEditMenuProps {
    actions: DashboardActionType[];
    onClickModal: (action: DashboardActionType) => () => void;
    isInPresentationMode: boolean;
    isFavourite: boolean;
    isLast: boolean;
    isFullWidth: boolean;
}

const DashboardEditMenuCategory = ({
    actions,
    onClickModal,
    isInPresentationMode,
    isFavourite,
    isLast,
    isFullWidth,
}: DashboardEditMenuProps) => (
    <>
        {actions.map((action) => (
            <DashboardEditMenuItem
                key={action}
                action={action}
                onClickModal={onClickModal}
                isInPresentationMode={isInPresentationMode}
                isFavourite={isFavourite}
                isFullWidth={isFullWidth}
            />
        ))}
        {!isLast && <Divider />}
    </>
);

export default DashboardEditMenuCategory;
